import { Box, IconButton } from "@mui/material";
import { GlobalButton, GlobalSecondaryButton } from "../../../styles";
import { useState } from "react";
import { Alert } from "../ProjectManagement/components/Alert";
import { useEffect } from "react";
import { styled } from "@mui/material/styles";
import { DataTableWrapper } from "../ProjectManagement/components/DataTableWrapper";
import { StyledTableCell, StyledTableRow } from "../../../../components/UXDataTable";
import { Delete } from "@mui/icons-material";
import ModalTwoButtons from "../../../../components/Modal/ModalTwoButtons";
import EmailAddressTextField from "../../../../components/EmailAddressField";
import UserHomeSite from "../../../../api/Admin/UserHomeSite";

const ConfirmRemoveUserAlert = styled(ModalTwoButtons)({});

const tableKey = "usersSiteTable";
const fetchError = "There was an error fetching users site.";
const addError = "There was an error adding user to site.";
const removeError = "There was an error removing user from site.";

const columns = [
  {
    id: "userEmail",
    label: "User Email",
    notSorteable: false
  }
];

const successfullyAddUserToSiteAlert = {
  title: "User Added",
  message: "User was successfully added to site.",
};

const successfullyRemovedUserFromSiteAlert = {
  title: "User Removed",
  message: "User was successfully removed from site.",
};

const errorSiteUsersAlert = (
  message, error) => ({
    title: "Error",
    message: message,
    details: `Reason: ${getNetworkErrorMessage(error)}`,
});

const getNetworkErrorMessage = (error) => {  
  return (error.message ??
    "The application has encountered an unknown error and cannot complete the action. Please contact a system administrator for help."
  );
}; 

const UserHomeSiteManagement = ({
  siteName,
  onClose
}) => {

  const [alert, setAlert] = useState(undefined);
  const [loading, setLoading] = useState(false);
  const [users, setUsers] = useState([]);
  const [deleteUserEmail, setDeleteUserEmail] = useState("");
  const [addUserEmail, setAddUserEmail] = useState("");
  const [userEmailError, setUserEmailError] = useState("");
  const [displayRemoveUserFromSiteAlert, setDisplayRemoveUserFromSiteAlert] = useState(false);
  const [refreshUsers, setRefreshUsers] = useState("");

  useEffect(() =>{
    setLoading(true);
    UserHomeSite.GetUsersByHomeSite(siteName)
    .then((response) =>
    {
      setUsers(response);                
    })
    .catch((err) =>
    {
      setAlert(errorSiteUsersAlert(fetchError, err));
    })
    .finally(() =>
    {
      setLoading(false);
    });
  }, [siteName, refreshUsers]);

  const reloadUsers = () => {
    setRefreshUsers(prev => !prev);
  };

  const handleSiteUserAddFormOnSave = (userEmail) => {
    setLoading(true);

    UserHomeSite.CreateOrUpdateUserHomeSite(userEmail, siteName)
      .then((_response) =>
      {
        reloadUsers();
        setAddUserEmail("");
        setAlert(successfullyAddUserToSiteAlert);               
      })
      .catch((err) =>
      {
        setAlert(errorSiteUsersAlert(addError, err));
      })
      .finally(() =>
      {
        setLoading(false);
      });
  };

  const handleRemoveUserAlertOkClick = () => {
    setDisplayRemoveUserFromSiteAlert(false);
    setLoading(true);

    UserHomeSite.DeleteUserHomeSite(deleteUserEmail)
      .then((_response) =>
      {
        reloadUsers();
        setAlert(successfullyRemovedUserFromSiteAlert);               
      })
      .catch((err) =>        
      {
        setAlert(errorSiteUsersAlert(removeError, err));
      })
      .finally(() =>
      {
        setLoading(false);
      });
  };

  const handleRemoveUserAlertCancelClick = () => setDisplayRemoveUserFromSiteAlert(false);

  const handleRemoveUserClick = (userEmail) => {
    setDeleteUserEmail(userEmail ?? "");
    setDisplayRemoveUserFromSiteAlert(true);
  };

  const handleAlertOkClick = () => {
    alert?.onOk?.();
    setAlert(undefined);
  };

  return(
    <>
      <Box sx={{ display: "inline-flex", marginTop:"10px", marginBottom:"10px", gap: "20px" }}>
        <EmailAddressTextField
          key="userEmail"
          validatedUserEmail={addUserEmail}
          setValidatedUserEmail={(value) => { setAddUserEmail(value) }}
          setHasErrors={setUserEmailError}
          hasErrors={userEmailError}
          isDisabled={false}
          fieldWidth="400px"
          labelText="User Email"
          placeholderText="User Email"
          size={"small"}
          margin={"5px"}
          fontSize={16}
          showPlusMeButton={false}
        />
        <GlobalButton
          width="20px"
          variant="contained"
          disabled={loading || addUserEmail === "" || userEmailError}              
          onClick={() => handleSiteUserAddFormOnSave(addUserEmail)}
        >
          Add User
        </GlobalButton>
      </Box>
      <DataTableWrapper
        tableId="users"
        columns={columns}
        tableWidth="100%"
        blankFirstHeader={true}
        noDataFoundMessage="No users assigned"
        isDataLoading={loading}
        rowData={users}
        rowComponent={(user, rowIndex) => (
          <StyledTableRow key={`${tableKey}-row-${rowIndex}`}>
            <StyledTableCell width={5}>
              <IconButton color="error" style={{padding:"4px"}} >
                <Delete fontSize="small" color="inherit"
                  onClick={() => handleRemoveUserClick(user?.userEmail)}
                  disabled={loading}
                />
              </IconButton>
            </StyledTableCell>
            <StyledTableCell>{user?.userEmail}</StyledTableCell>                    
          </StyledTableRow>
        )}
      />

      <Box display="flex" flexDirection={"row"} alignItems="center" justifyContent={"center"} textAlign={"center"}>                                               
        <GlobalSecondaryButton
          variant="contained"
          onClick={() => {
            onClose?.();
        }}>
        Close
        </GlobalSecondaryButton>
      </Box>
      <ConfirmRemoveUserAlert
        title="Remove user"
        button1Text="OK"
        button1Action={handleRemoveUserAlertOkClick}
        isButton1Disabled={false}
        button2Text="Cancel"
        button2Action={handleRemoveUserAlertCancelClick}
        isButton2Disabled={false}
        open={displayRemoveUserFromSiteAlert}
        setOpen={undefined}
        setClose={undefined}>
        <Box>Are you sure you want to remove {deleteUserEmail} from site: {siteName}?</Box>
      </ConfirmRemoveUserAlert>         
      <Alert
        title={alert?.title}
        message={alert?.message}
        details={alert?.details}
        display={alert !== undefined}
        onOk={handleAlertOkClick}
      />
    </>
  );
};

export default UserHomeSiteManagement;